import DeleteIcon from '@mui/icons-material/Delete';

import Button from '@mui/material/Button';
import CancelIcon from '@mui/icons-material/Cancel';
import CircularProgress  from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import React, { ReactElement, useEffect } from 'react';

import {  uuid } from '../../../../utils/utils';
import { toast } from 'react-toastify';
import { useWorkspaceContext } from '../../../../context/workspaceContext';
import { CanvasEnvironment } from '../../../../utils/canvas/canvas-environment';
import { ResourceType } from '../../../../@types/resource-type';
import * as CanvasActions from '../../../../utils/canvas/canvas-actions';
import { resolveFaultTreeContainer } from '../../../../utils/canvas/fault-tree-container';
import { Checkbox, FormControl, FormControlLabel, FormGroup } from '@mui/material';
import { FaultTreeNode } from '../../../../@types/faultTreeNode';




function PaperComponent(props: PaperProps): ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

export default function DeleteFaultTreeNodeModal(props: { open: boolean, env: CanvasEnvironment | null, onClose: () => void }): ReactElement {
    const { open, env, onClose } = props;
    const [uniqueId, setUniqueId] = React.useState<string>('');    
    const [hasChildren, setHasChildren] = React.useState<boolean>(false);
    const [children, setChildren] = React.useState<FaultTreeNode[]>([]);
    const [isTopNode, setIsTopNode] = React.useState<boolean>(false);
    const [preservedChildrenIds, setPreservedChildrenIds] = React.useState<number[]>([]);
    const { setCurrentWorkspaceObject } = useWorkspaceContext();
    
    const [isRunning, setIsRunning] = React.useState<boolean>(false);
    const uuidRef = React.useRef(uuid());

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        let idx = preservedChildrenIds.indexOf(parseInt(event.target.value));
        if (idx === -1) {
            setPreservedChildrenIds([...preservedChildrenIds, parseInt(event.target.value)]);
        } else {
            setPreservedChildrenIds(preservedChildrenIds.filter((id) => id !== parseInt(event.target.value)));
        }
    }

    useEffect(() => {
        if (open) {
            const [selectedNode] = env!.selection
            setUniqueId(selectedNode.get('data').uniqueId);
            let childIds = env!.selection[0].get('data').childIds;
            let parentId = env!.selection[0].get('data').parentId;
            setIsTopNode(parentId === null || parentId === undefined);
            setHasChildren(parentId && childIds && childIds.length > 0);
            let childNodes: FaultTreeNode[] = [];
            if (parentId && childIds && childIds.length > 0) {       
                for (let childId of childIds) {
                    const ftElement = env!.graph.getCell(`ft-${childId}`);
                    if (ftElement) {
                        childNodes.push(ftElement.get('data'));
                    }
                }          
            }
            setChildren(childNodes);
            setIsRunning(false);      
        }
    }, [open]);

    const handleSave = async (): Promise<any> => {
        const [selectedNode] = env!.selection

        return resolveFaultTreeContainer(env)!.deleteNode(selectedNode.get('data').id, preservedChildrenIds);
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            fullWidth
            maxWidth="sm">
            <DialogTitle style={{ cursor: 'move'}} id='draggable-dialog-title'>
                {'Delete Fault Tree Node ' + `${uniqueId}`}
            </DialogTitle>
            <DialogContent dividers>
                <FormGroup>
                    <DialogContentText id={`fault-tree-delete-description-${uuidRef.current}`}>
                        {hasChildren ?
                            <span>The fault tree node you selected has children. <span style={{ color: 'red' }}>Check the children that you would like to preserve!</span></span>
                            : (isTopNode ? <span>Are you sure you want to <span style={{ color: 'red' }}><strong>delete the entire fault tree irreversibly</strong></span>?</span>
                                : <span>Are you sure you want to delete this fault tree node?</span>)}

                    </DialogContentText>
                    <hr />
                    <FormControl>
                        {children.map((child) => (
                            <FormControlLabel
                                key={child.id}
                                control={<Checkbox checked={preservedChildrenIds.includes(child.id)} onChange={handleChange} value={child.id.toString()} />}
                                label={`${child.uniqueId}`}
                            />
                        ))}
                    </FormControl>

                </FormGroup>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<CancelIcon />}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={isRunning ? <CircularProgress size={20} /> : <DeleteIcon />}
                    onClick={async (): Promise<any> => {
                        setIsRunning(true);
                        try {
                            const response = await handleSave();
                            env!.isDirty = true;
                            setCurrentWorkspaceObject({
                                objectType: resolveFaultTreeContainer(env)?.objectType ?? ResourceType.EVENT_SEQUENCE_TREES,
                                object: response,
                            });
                        } catch (error: any) {
                            toast.error(error.message);
                        } finally {
                            setIsRunning(false);
                            onClose();
                        }                                    
                    }}
                    disabled={!CanvasActions.canAddOrEditResource(env) || isRunning}
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    )
   
}